import * as THREE from 'three';
import { EscortBoat } from '../entities/EscortBoat.js';

const ALLY_ATTACK_BY_LEVEL = [5, 7, 9, 12, 14, 16, 18, 20, 22, 24];
const FORMATION_OFFSETS = [
    new THREE.Vector2(0, -4.5),
    new THREE.Vector2(-1.5, -5.5),
    new THREE.Vector2(1.5, -5.5),
    new THREE.Vector2(-2.5, -7.5),
    new THREE.Vector2(2.5, -7.5)
];

export class RescueManager {
    constructor(game) {
        this.game = game;
        this.sceneManager = game.sceneManager;

        this.islands = [];
        this.followers = [];
        this.followerSlotsUsed = 0;

        this.maxFollowers = 5;
        this.rescueRange = 10;
        this.rescueDuration = 3.5; // seconds
        this.respawnInterval = 120; // seconds

        this.elapsedTime = 0;
        this.rescueProgress = 0;
        this.activeIsland = null;
        this.uiState = 'hidden';
        this.isRescuing = false;
        this.rescueActionBound = false;
        this.rightClickListener = null;
        this.contextMenuListener = null;
        this.rightClickBound = false;

        this.lifeVersion = 0;
    }

    async init() {
        this.refreshIslandData();
        this.bindRescueAction();
    }

    refreshIslandData() {
        if (!this.sceneManager || typeof this.sceneManager.getAmbientIslands !== 'function') {
            this.islands = [];
            return;
        }

        const islands = this.sceneManager.getAmbientIslands();
        this.islands = islands.map((data) => ({
            id: data.id,
            mesh: data.mesh,
            resident: data.resident || null,
            available: !!(data.resident && data.resident.visible !== false),
            nextRespawnTime: 0
        }));
    }

    bindRescueAction() {
        const ui = window.gameApp?.uiManager;
        if (!ui || typeof ui.setRescueAction !== 'function') return;
        ui.setRescueAction(() => this.startRescue());
        this.rescueActionBound = true;
    }

    startRescue(event = null) {
        if (event) {
            event.preventDefault?.();
            event.stopPropagation?.();
        }
        if (!this.activeIsland || this.isRescuing) return;
        if (this.followerSlotsUsed >= this.maxFollowers) {
            const t = this.game.languageManager.t.bind(this.game.languageManager);
            this.showRescueMessage(t('rescue.full', { max: this.maxFollowers }));
            return;
        }

        // 检查是否需要显示奖励视频（由RewardManager管理）
        if (this.game.rewardManager && this.game.rewardManager.shouldShowVideo()) {
            console.log('📺 救援需要观看奖励视频');
            // 显示奖励视频，完成后继续救援
            this.game.rewardManager.showVideo(() => {
                // 视频完成后的回调：继续救援流程
                this.proceedWithRescue();
            });
        } else {
            // 不需要视频，直接救援
            this.proceedWithRescue();
        }

        // 增加救援计数（奖励视频系统需要）
        if (this.game.rewardManager) {
            this.game.rewardManager.incrementRescueCount();
        }
    }

    /**
     * 继续救援流程（奖励视频播放完成后或无需视频时调用）
     */
    proceedWithRescue() {
        this.isRescuing = true;
        this.unbindRightClickRescue();
        this.rescueProgress = 0;
        const t = this.game.languageManager.t.bind(this.game.languageManager);
        this.showRescueProgress(0, t('rescue.progress', { current: this.followerSlotsUsed, max: this.maxFollowers }));
    }


    bindRightClickRescue() {
        const ui = window.gameApp?.uiManager;
        if (!ui || ui.isMobileUI || this.rightClickBound) return;
        if (typeof window === "undefined") return;

        this.rightClickListener = (event) => {
            if (event?.button === 2) {
                this.startRescue(event);
            }
        };

        this.contextMenuListener = (event) => {
            this.startRescue(event);
        };

        window.addEventListener('pointerup', this.rightClickListener, true);
        window.addEventListener('contextmenu', this.contextMenuListener, true);
        this.rightClickBound = true;
    }

    unbindRightClickRescue() {
        if (!this.rightClickBound) return;
        if (typeof window === "undefined") {
            this.rightClickListener = null;
            this.contextMenuListener = null;
            this.rightClickBound = false;
            return;
        }
        if (this.rightClickListener) {
            window.removeEventListener('pointerup', this.rightClickListener, true);
        }
        if (this.contextMenuListener) {
            window.removeEventListener('contextmenu', this.contextMenuListener, true);
        }
        this.rightClickListener = null;
        this.contextMenuListener = null;
        this.rightClickBound = false;
    }

    update(deltaTime) {
        if (!this.game.isRunning || !this.game.player) return;

        if (!this.islands.length) {
            this.refreshIslandData();
            if (!this.islands.length) return;
        }

        if (!this.rescueActionBound) {
            this.bindRescueAction();
        }

        this.elapsedTime += deltaTime;
        this.updateRespawns();

        const capacityReached = this.followerSlotsUsed >= this.maxFollowers;
        const candidate = this.findNearestRescueIsland();

        if (capacityReached) {
            this.rescueProgress = 0;
            this.isRescuing = false;
            if (candidate) {
                const t = this.game.languageManager.t.bind(this.game.languageManager);
                this.showRescueMessage(t('rescue.full', { max: this.maxFollowers }));
            } else if (this.uiState !== 'hidden') {
                this.hideRescueUI();
            }
            this.activeIsland = candidate || null;
            return;
        }

        if (!candidate) {
            this.clearRescueState();
            return;
        }

        if (this.activeIsland !== candidate) {
            this.activeIsland = candidate;
            this.rescueProgress = 0;
            this.isRescuing = false;
        }

        if (!this.isRescuing) {
            const t = this.game.languageManager.t.bind(this.game.languageManager);
            this.showRescuePrompt(t('rescue.prompt', { current: this.followerSlotsUsed, max: this.maxFollowers }));
            return;
        }

        this.rescueProgress += deltaTime / this.rescueDuration;
        const clampedProgress = Math.min(this.rescueProgress, 1);
        const t = this.game.languageManager.t.bind(this.game.languageManager);
        this.showRescueProgress(clampedProgress, t('rescue.progress', { current: this.followerSlotsUsed, max: this.maxFollowers }));

        if (clampedProgress >= 1) {
            const island = this.activeIsland;
            this.completeRescue(island);
            this.rescueProgress = 0;
            this.isRescuing = false;
            this.activeIsland = null;
            this.hideRescueUI();
        }
    }

    updateRespawns() {
        this.islands.forEach((island) => {
            if (island.available) return;
            if (this.elapsedTime >= island.nextRespawnTime) {
                island.available = true;
                if (island.resident) {
                    island.resident.visible = true;
                }
            }
        });
    }

    findNearestRescueIsland() {
        const playerBody = this.game.player?.body;
        if (!playerBody) return null;

        const origin = playerBody.position;
        let nearest = null;
        let bestDistanceSq = this.rescueRange * this.rescueRange;

        const worldPos = new THREE.Vector3();
        this.islands.forEach((island) => {
            if (!island.available || !island.mesh) return;
            if (!island.resident || island.resident.visible === false) return;

            island.mesh.getWorldPosition(worldPos);
            const dx = worldPos.x - origin.x;
            const dz = worldPos.z - origin.z;
            const distSq = dx * dx + dz * dz;
            if (distSq <= bestDistanceSq) {
                bestDistanceSq = distSq;
                nearest = island;
            }
        });

        return nearest;
    }

    completeRescue(island) {
        if (!island) return;

        island.available = false;
        island.nextRespawnTime = this.elapsedTime + this.respawnInterval;
        if (island.resident) {
            island.resident.visible = false;
        }

        this.followerSlotsUsed = Math.min(this.maxFollowers, this.followerSlotsUsed + 1);
        this.spawnEscortBoat(island).catch((error) => {
            console.error('Failed to spawn escort boat:', error);
            this.followerSlotsUsed = Math.max(0, this.followerSlotsUsed - 1);
        });
    }

    async spawnEscortBoat(island) {
        const lifeVersionAtSpawn = this.lifeVersion;

        const startPos = new THREE.Vector3();
        if (this.game.player?.body?.position) {
            startPos.copy(this.game.player.body.position);
        } else if (island.resident) {
            island.resident.getWorldPosition(startPos);
        }
        startPos.y = 0.12;

        const attackPower = this.getAttackForLevel(this.game.player?.level || 1);
        const escort = new EscortBoat({
            game: this.game,
            manager: this,
            attackPower,
            formationIndex: this.followers.length,
            ownerShip: this.game.player // 明确指定跟随玩家，避免引用丢失
        });

        await escort.init(startPos);

        if (lifeVersionAtSpawn !== this.lifeVersion) {
            escort.manager = null;
            escort.dispose?.();
            this.followerSlotsUsed = Math.min(this.maxFollowers, this.followers.length);
            return;
        }

        const escortId = `ally_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
        escort.id = escortId;
        this.game.addGameObject(escortId, escort);
        this.followers.push(escort);
        this.followerSlotsUsed = Math.min(this.maxFollowers, Math.max(this.followerSlotsUsed, this.followers.length));
        this.realignFormation();
    }

    getAttackForLevel(level) {
        const idx = Math.min(Math.max(Math.round(level), 1), 10) - 1;
        return ALLY_ATTACK_BY_LEVEL[idx];
    }

    getFormationOffset(index) {
        if (index >= 0 && index < FORMATION_OFFSETS.length) {
            return FORMATION_OFFSETS[index];
        }
        const fallBack = new THREE.Vector2(0, -4.5 - index * 1.25);
        return fallBack;
    }

    showRescuePrompt(label) {
        const ui = window.gameApp?.uiManager;
        if (ui && typeof ui.showRescuePrompt === 'function') {
            const t = this.game.languageManager.t.bind(this.game.languageManager);
            const buttonLabel = ui.isMobileUI ? t('rescue.buttonMobile') : t('rescue.buttonDesktop');
            ui.showRescuePrompt(label, buttonLabel);
            if (!ui.isMobileUI) {
                this.bindRightClickRescue();
            }
            this.uiState = 'prompt';
        } else {
            this.showRescueMessage(label);
        }
    }

    showRescueProgress(progress, label) {
        const ui = window.gameApp?.uiManager;
        if (ui) {
            ui.showRescueProgress(progress, label);
            this.uiState = 'progress';
        }
    }

    showRescueMessage(message) {
        const ui = window.gameApp?.uiManager;
        if (ui) {
            ui.showRescueMessage(message);
            this.uiState = 'message';
        }
    }

    hideRescueUI() {
        const ui = window.gameApp?.uiManager;
        if (ui) {
            ui.hideRescueStatus();
            this.uiState = 'hidden';
        }
        this.unbindRightClickRescue();
    }

    clearRescueState() {
        this.isRescuing = false;
        this.rescueProgress = 0;
        this.activeIsland = null;
        this.unbindRightClickRescue();
        if (this.uiState !== 'hidden') {
            this.hideRescueUI();
        }
    }

    realignFormation() {
        this.followers = this.followers.filter(Boolean);
        this.followers.forEach((escort, index) => escort.setFormationIndex(index));
    }

    handleFollowerDestroyed(escort) {
        const index = this.followers.indexOf(escort);
        if (index !== -1) {
            this.followers.splice(index, 1);
        }
        this.followerSlotsUsed = Math.max(0, this.followerSlotsUsed - 1);
        this.realignFormation();
    }

    onPlayerDeath() {
        this.lifeVersion += 1;

        const existingFollowers = [...this.followers];
        existingFollowers.forEach((escort) => {
            if (!escort) return;
            if (escort.id) {
                this.game.removeGameObject(escort.id);
            } else {
                escort.dispose?.();
            }
        });
        this.followers = [];
        this.followerSlotsUsed = 0;
        this.isRescuing = false;
        this.clearRescueState();
        this.unbindRightClickRescue();
    }
}
